/**
 * Ticket Detail Component
 *
 * Shows ticket details with message thread and reply form.
 */

import { useState } from "react";
import { format } from "date-fns";
import {
  ArrowLeft,
  Send,
  Loader2,
  RefreshCw,
  MessageSquare,
} from "lucide-react";
import { Button } from "../ui/button";
import { useTicket, useAddMessage } from "../../hooks/useSupport";
import { STATUS_LABELS, CATEGORY_LABELS } from "../../types/support";
import type { SupportTicketMessage } from "../../types/support";

interface TicketDetailProps {
  ticketId: string;
  onBack: () => void;
}

export function TicketDetail({ ticketId, onBack }: TicketDetailProps) {
  const { data: ticket, isLoading, refetch } = useTicket(ticketId);
  const [replyContent, setReplyContent] = useState("");
  const addMessage = useAddMessage(ticketId);

  const handleSendReply = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!replyContent.trim()) return;

    try {
      await addMessage.mutateAsync({ content: replyContent.trim() });
      setReplyContent("");
    } catch (err) {
      console.error("Failed to send reply:", err);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Ticket not found</p>
        <Button variant="ghost" onClick={onBack} className="mt-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to tickets
        </Button>
      </div>
    );
  }

  const canReply = ticket.status !== "closed";

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <button
            onClick={onBack}
            className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to tickets
          </button>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground font-mono">
              {ticket.ticket_number}
            </span>
            <StatusBadge status={ticket.status} />
          </div>
          <h1 className="text-xl font-semibold text-foreground mt-1">
            {ticket.subject}
          </h1>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => refetch()}
          title="Refresh"
        >
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {/* Ticket Info */}
      <div className="bg-card border border-border rounded-lg p-4">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-muted-foreground">Category:</span>{" "}
            <span className="text-foreground">
              {CATEGORY_LABELS[ticket.category]}
            </span>
          </div>
          <div>
            <span className="text-muted-foreground">Status:</span>{" "}
            <span className="text-foreground">
              {STATUS_LABELS[ticket.status]}
            </span>
          </div>
          <div>
            <span className="text-muted-foreground">Created:</span>{" "}
            <span className="text-foreground">
              {format(new Date(ticket.created_at), "MMM d, yyyy h:mm a")}
            </span>
          </div>
          <div>
            <span className="text-muted-foreground">Updated:</span>{" "}
            <span className="text-foreground">
              {format(new Date(ticket.updated_at), "MMM d, yyyy h:mm a")}
            </span>
          </div>
        </div>
      </div>

      {/* Description */}
      <div className="bg-card border border-border rounded-lg p-4">
        <h2 className="text-sm font-medium text-muted-foreground mb-2">
          Description
        </h2>
        <p className="text-foreground whitespace-pre-wrap">
          {ticket.description}
        </p>
      </div>

      {/* Messages */}
      <div className="bg-card border border-border rounded-lg p-4">
        <h2 className="text-sm font-medium text-muted-foreground mb-4 flex items-center gap-2">
          <MessageSquare className="w-4 h-4" />
          Conversation ({ticket.message_count} messages)
        </h2>
        {ticket.messages.length === 0 ? (
          <p className="text-muted-foreground text-center py-4">
            No messages yet. Our support team will respond soon.
          </p>
        ) : (
          <div className="space-y-4">
            {ticket.messages.map((message) => (
              <MessageItem key={message.id} message={message} />
            ))}
          </div>
        )}
      </div>

      {/* Reply Form */}
      {canReply ? (
        <form
          onSubmit={handleSendReply}
          className="bg-card border border-border rounded-lg p-4"
        >
          <h2 className="text-sm font-medium text-muted-foreground mb-2">
            Add Reply
          </h2>
          <textarea
            value={replyContent}
            onChange={(e) => setReplyContent(e.target.value)}
            placeholder="Type your reply..."
            rows={4}
            maxLength={10000}
            className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
          />
          <div className="flex justify-end mt-3">
            <Button
              type="submit"
              disabled={addMessage.isPending || !replyContent.trim()}
            >
              {addMessage.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Reply
                </>
              )}
            </Button>
          </div>
        </form>
      ) : (
        <div className="bg-muted/50 border border-border rounded-lg p-4 text-center text-muted-foreground">
          This ticket has been closed and cannot receive new messages.
        </div>
      )}
    </div>
  );
}

function StatusBadge({
  status,
}: {
  status:
    | "open"
    | "in_progress"
    | "waiting_on_customer"
    | "resolved"
    | "closed";
}) {
  const colors: Record<typeof status, string> = {
    open: "bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200",
    in_progress:
      "bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200",
    waiting_on_customer:
      "bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200",
    resolved:
      "bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200",
    closed: "bg-muted text-muted-foreground",
  };

  return (
    <span
      className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors[status]}`}
    >
      {STATUS_LABELS[status]}
    </span>
  );
}

function MessageItem({ message }: { message: SupportTicketMessage }) {
  const isAdmin = message.is_admin_message;

  return (
    <div
      className={`p-3 rounded-lg ${
        isAdmin
          ? "bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800"
          : "bg-muted/50 border border-border"
      }`}
    >
      <div className="flex justify-between items-start mb-2">
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm text-foreground">
            {message.sender_name || message.sender_email || "You"}
          </span>
          {isAdmin && (
            <span className="text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded">
              Support
            </span>
          )}
        </div>
        <span className="text-xs text-muted-foreground">
          {format(new Date(message.created_at), "MMM d, h:mm a")}
        </span>
      </div>
      <p className="text-sm text-foreground whitespace-pre-wrap">
        {message.content}
      </p>
    </div>
  );
}
